import React, { useEffect, useState } from 'react';
import {
  Users,
  Building2,
  UserCheck,
  Search,
  Filter,
  Calendar,
  Laptop,
  Globe,
  ChevronRight,
  RefreshCw,
  Wifi,
  AlertCircle
} from 'lucide-react';
import { api as apiClient, ws } from '../../lib/api';
import { topics } from '@sahaj/websocket';

export interface EmployeeSummary {
  userId: string;
  email: string;
  displayName: string;
  avatarUrl?: string | null;
  workspaceId: string;
  workspaceName: string;
  registeredOwner: string;
  online: boolean;
  idle: boolean;
  currentState: string;
  currentAppName?: string | null;
  currentDomain?: string | null;
  activeSeconds: number;
  idleSeconds: number;
  deviceCount: number;
  lastSeenAt?: string | null;
}

export interface WorkspaceOption {
  id: string;
  name: string;
  ownerName?: string;
}

interface WorkforceEmployeeListViewProps {
  workspaces: WorkspaceOption[];
  selectedWorkspaceId: string;
  onSelectWorkspace: (wsId: string) => void;
  onSelectEmployee: (userId: string, dateStr?: string) => void;
}

export const WorkforceEmployeeListView: React.FC<WorkforceEmployeeListViewProps> = ({
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
  onSelectEmployee,
}) => {
  const [employees, setEmployees] = useState<EmployeeSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [ownerFilter, setOwnerFilter] = useState<string>('ALL');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0] || '');
  const [isRealtime, setIsRealtime] = useState<boolean>(ws.getState() === 'CONNECTED');

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.workforce.getEmployees(
        selectedWorkspaceId || undefined,
        undefined,
        statusFilter !== 'ALL' ? statusFilter : undefined,
        selectedDate
      );
      setEmployees(data || []);
    } catch (err: any) {
      console.error('Failed to fetch workforce employees:', err);
      setError(err?.message || 'Unable to load workforce employees.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const doFetch = async (isSilent = false) => {
      try {
        if (!isSilent) setLoading(true);
        setError(null);
        const data = await apiClient.workforce.getEmployees(
          selectedWorkspaceId || undefined,
          undefined,
          statusFilter !== 'ALL' ? statusFilter : undefined,
          selectedDate
        );
        if (isMounted) {
          setEmployees(data || []);
        }
      } catch (err: any) {
        if (isMounted && !isSilent) {
          console.error('Failed to fetch workforce employees:', err);
          setError(err?.message || 'Unable to load workforce employees.');
        }
      } finally {
        if (isMounted && !isSilent) {
          setLoading(false);
        }
      }
    };

    doFetch();

    const unsubState = ws.onStateChange((state) => {
      setIsRealtime(state === 'CONNECTED');
    });

    const unsubTopic = selectedWorkspaceId
      ? ws.subscribe(topics.workforceLiveState(selectedWorkspaceId), () => {
          doFetch(true);
        })
      : () => {};

    const intervalId = setInterval(() => {
      doFetch(true);
    }, 5000);

    return () => {
      isMounted = false;
      unsubState();
      unsubTopic();
      clearInterval(intervalId);
    };
  }, [selectedWorkspaceId, selectedDate, statusFilter]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const registeredOwners = Array.from(new Set(employees.map((e) => e.registeredOwner).filter(Boolean)));

  const filteredEmployees = employees.filter((e) => {
    if (ownerFilter !== 'ALL' && e.registeredOwner !== ownerFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchName = e.displayName?.toLowerCase().includes(q);
      const matchEmail = e.email?.toLowerCase().includes(q);
      if (!matchName && !matchEmail) return false;
    }
    return true;
  });

  const onlineCount = employees.filter(
    (e) => (e.online && !e.idle) || e.currentState === 'ONLINE_ACTIVE'
  ).length;
  const idleCount = employees.filter(
    (e) => (e.online && e.idle) || e.currentState === 'ONLINE_IDLE'
  ).length;
  const offlineCount = employees.filter(
    (e) => !e.online && e.currentState !== 'ONLINE_ACTIVE' && e.currentState !== 'ONLINE_IDLE'
  ).length;

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds <= 0) return '0m';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Top Controls & Filter Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Workplace Selector */}
          <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-3 py-2 rounded-lg text-xs">
            <Building2 className="w-4 h-4 text-indigo-400 shrink-0" />
            <span className="text-slate-400 font-medium">Workplace:</span>
            <select
              value={selectedWorkspaceId || ''}
              onChange={(e) => onSelectWorkspace(e.target.value)}
              className="bg-transparent text-slate-100 font-semibold focus:outline-none cursor-pointer"
            >
              <option value="" className="bg-slate-900 text-slate-200">
                All Workplaces
              </option>
              {workspaces.map((ws) => (
                <option key={ws.id} value={ws.id} className="bg-slate-900 text-slate-200">
                  {ws.name}
                </option>
              ))}
            </select>
          </div>

          {/* Registered Owner Filter */}
          <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-3 py-2 rounded-lg text-xs">
            <UserCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-slate-400 font-medium">Owner:</span>
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className="bg-transparent text-slate-100 font-semibold focus:outline-none cursor-pointer"
            >
              <option value="ALL" className="bg-slate-900 text-slate-200">
                All Owners
              </option>
              {registeredOwners.map((owner) => (
                <option key={owner} value={owner} className="bg-slate-900 text-slate-200">
                  {owner}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search employee name or email..."
              value={search}
              onChange={handleSearchChange}
              className="w-full bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-500 text-xs rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-3 py-2 rounded-lg text-xs">
            <Filter className="w-4 h-4 text-amber-400 shrink-0" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent text-slate-100 font-semibold focus:outline-none cursor-pointer"
            >
              <option value="ALL" className="bg-slate-900 text-slate-200">
                All Statuses
              </option>
              <option value="ONLINE" className="bg-slate-900 text-slate-200">
                Online Active
              </option>
              <option value="IDLE" className="bg-slate-900 text-slate-200">
                Online Idle
              </option>
              <option value="OFFLINE" className="bg-slate-900 text-slate-200">
                Offline
              </option>
            </select>
          </div>

          {/* Date Picker */}
          <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-3 py-2 rounded-lg text-xs text-slate-300">
            <Calendar className="w-4 h-4 text-indigo-400 shrink-0" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent text-slate-100 font-semibold focus:outline-none cursor-pointer"
            />
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchEmployees}
            className="p-2 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-lg text-slate-400 hover:text-slate-200 transition-colors"
            title="Refresh workforce list"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Overview Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Total Workforce</span>
            <Users className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">{employees.length}</div>
        </div>

        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Active Now</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
          </div>
          <div className="text-2xl font-bold text-emerald-400">{onlineCount}</div>
        </div>

        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Idle</span>
            <span className="w-2 h-2 rounded-full bg-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-400">{idleCount}</div>
        </div>

        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Offline / No Telemetry</span>
            <span className="w-2 h-2 rounded-full bg-slate-600" />
          </div>
          <div className="text-2xl font-bold text-slate-400">{offlineCount}</div>
        </div>
      </div>

      {/* Employee List Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Employees ({filteredEmployees.length})
          </h2>
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            <Wifi className={`w-3.5 h-3.5 ${isRealtime ? 'text-emerald-500 animate-pulse' : 'text-slate-400'}`} />
            {isRealtime ? 'WebSocket Live' : 'REST Polling'}
          </span>
        </div>

        {error ? (
          <div className="p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl flex items-center gap-2 text-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading ? (
          <div className="p-8 text-center bg-slate-900/50 border border-slate-800 rounded-xl text-slate-400">
            Loading real workforce telemetry data...
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="p-8 text-center bg-slate-900/50 border border-slate-800 rounded-xl text-slate-400 space-y-2">
            <Users className="w-8 h-8 text-slate-600 mx-auto" />
            <p className="text-sm font-medium">No employees found in this workspace matching filter criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEmployees.map((emp) => {
              const isActive = emp.currentState === 'ONLINE_ACTIVE' || (emp.online && !emp.idle);
              const isIdle = emp.currentState === 'ONLINE_IDLE' || (emp.online && emp.idle);
              const isStale = emp.currentState === 'STALE';

              return (
                <div
                  key={`${emp.workspaceId}-${emp.userId}`}
                  onClick={() => onSelectEmployee(emp.userId, selectedDate)}
                  className="p-5 bg-slate-900 border border-slate-800 hover:border-indigo-500/50 rounded-xl space-y-4 cursor-pointer transition-all hover:shadow-lg hover:shadow-indigo-500/5 group"
                >
                  {/* Employee Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center font-bold text-indigo-300 text-sm shrink-0">
                        {emp.displayName ? emp.displayName.substring(0, 2).toUpperCase() : 'EM'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-100 text-sm group-hover:text-indigo-400 transition-colors truncate">
                          {emp.displayName || 'Employee'}
                        </h3>
                        <p className="text-xs text-slate-400 truncate">{emp.email}</p>
                      </div>
                    </div>

                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap shrink-0 ${
                        isActive
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : isIdle
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : isStale
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : 'bg-slate-800 text-slate-400 border border-slate-700'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          isActive ? 'bg-emerald-400 animate-ping' : isIdle || isStale ? 'bg-amber-400' : 'bg-slate-500'
                        }`}
                      />
                      {emp.currentState || (emp.online ? 'ONLINE' : 'OFFLINE')}
                    </span>
                  </div>

                  {/* Workplace & Owner */}
                  <div className="text-xs text-slate-400 space-y-1 bg-slate-950/60 p-2.5 rounded-lg border border-slate-800/80">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Workplace:</span>
                      <span className="font-medium text-slate-300">{emp.workspaceName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Owner:</span>
                      <span className="font-medium text-slate-300">{emp.registeredOwner}</span>
                    </div>
                  </div>

                  {/* Current Telemetry Activity */}
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Laptop className="w-4 h-4 text-indigo-400 shrink-0" />
                      <span className="font-medium text-slate-200">
                        {emp.currentAppName || (emp.online ? 'UNKNOWN' : '—')}
                      </span>
                    </div>

                    {emp.currentDomain && (
                      <div className="flex items-center gap-2 text-slate-400 font-mono text-[11px] bg-indigo-500/5 px-2 py-1 rounded border border-indigo-500/10">
                        <Globe className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                        <span className="truncate">{emp.currentDomain}</span>
                      </div>
                    )}
                  </div>

                  {/* Duration Counters & Footer */}
                  <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="text-[10px] text-slate-500 block">ACTIVE</span>
                        <span className="font-mono font-semibold text-emerald-400">
                          {formatDuration(emp.activeSeconds)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 block">IDLE</span>
                        <span className="font-mono font-semibold text-amber-400">
                          {formatDuration(emp.idleSeconds)}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectEmployee(emp.userId, selectedDate);
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 rounded-lg font-medium text-xs transition-colors"
                    >
                      <span>View Activity</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkforceEmployeeListView;
